import { shadingLightestColor } from "Common/componentUtils/modalUtils";
import Button from "Components/button";
import Scrollbars from "Components/scrollbars";
import WelcomePanel from "Components/welcomePanel";
import WithModal from "Components/withModal";
import { Popover } from "Components/withPopover";
import classNames from "classnames";
import { CheckBox } from "devextreme-react/check-box";
import TextBox from "devextreme-react/text-box";
import debounce from "lodash/debounce";
import filter from "lodash/filter";
import includes from "lodash/includes";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import isString from "lodash/isString";
import isUndefined from "lodash/isUndefined";
import map from "lodash/map";
import reject from "lodash/reject";
import toLower from "lodash/toLower";
import toString from "lodash/toString";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuid } from "uuid";
import locale from "./menuList.locale";
import Styled from "./menuList.styled";
const defaultSubMenuMenuPosition = {
    my: "left top",
    at: "right top",
    offset: "0 -1",
    collision: "flip fit"
};
const subMenuAnimation = {
    hide: { type: "fadeOut", delay: 250, duration: 200 },
    show: { type: "fadeIn" }
};
export default function MenuList(props) {
    const menuId = useMemo(() => `menu_${uuid()}`, []);
    const modalShownCompletedRef = useRef(false);
    const subMenuSettingRefs = useRef({});
    const [searchText, setSearchText] = useState(props.searchInputText || "");
    const [loweredDebouncedSearchText, setLoweredDebouncedSearchText] = useState(toLower(searchText));
    const [modal, setModal] = useState({
        title: "",
        callback: null,
        header: ""
    });
    const { className, height, maxHeight, minHeight, maxWidth, minWidth, onSelectedChanged, keySelector, textSelector, visibilitySelector, items, selectedKey, showSearchInputWhenItemsLengthIsGreaterThan, itemComponent: MenuItemComponent, allMenuItem, allMenuItemText, itemNoWrapText, itemClassName, multi, menuRef, searchInputText, subMenuPopoverClassName, subMenuZIndex, searchInputMargins, emptyMessage, withApplyButton, onApply, onCancel, searchStylingMode = "filled", ...other } = props;
    useEffect(() => {
        if (!isUndefined(searchInputText)) {
            setSearchText(searchInputText);
            setLoweredDebouncedSearchText(toLower(searchInputText));
        }
    }, [searchInputText]);
    useEffect(() => {
        if (props.menuRef) {
            if (!props.menuRef.current)
                props.menuRef.current = {};
            props.menuRef.current.setSearchText = (text) => {
                setSearchText(text);
                setLoweredDebouncedSearchText(toLower(text));
            };
        }
    }, []);
    const handleModalHide = useCallback(() => {
        setModal(prev => ({
            ...prev,
            callback: null
        }));
        modalShownCompletedRef.current = false;
        subMenuSettingRefs.current = {};
    }, []);
    const getIsActive = (key) => multi
        ? includes(selectedKey, key) || (isUndefined(key) && isEmpty(selectedKey))
        : selectedKey === key;
    const buildHandleMenuItemClick = useCallback((key, active, data, subMenuSettingRefKey) => (evt) => {
        const menuItem = data;
        if (menuItem === null || menuItem === void 0 ? void 0 : menuItem.href)
            return;
        evt.preventDefault();
        if (menuItem) {
            if (menuItem.disabled)
                return;
            if (menuItem.onClick) {
                menuItem.onClick(evt, menuItem);
                return;
            }
            if (menuItem.confirm) {
                setModal({
                    title: menuItem.confirm.title,
                    header: menuItem.confirm.header,
                    callback: menuItem.confirm.callback
                });
                return;
            }
            if (menuItem.subMenu) {
                evt.stopPropagation();
                if (subMenuSettingRefKey && subMenuSettingRefs.current[subMenuSettingRefKey])
                    subMenuSettingRefs.current[subMenuSettingRefKey].hideOnMouseLeave =
                        !subMenuSettingRefs.current[subMenuSettingRefKey].hideOnMouseLeave;
                return;
            }
        }
        if (multi) {
            evt.stopPropagation();
            const isAll = isUndefined(data);
            const newActive = !active;
            if (isAll && !newActive)
                return;
            let newSelected = [];
            if (!isAll) {
                if (!newActive) {
                    newSelected = reject(selectedKey, el => el === key);
                }
                else {
                    newSelected = !isUndefined(key)
                        ? [...(selectedKey || []), key]
                        : selectedKey;
                }
            }
            onSelectedChanged && onSelectedChanged(newSelected);
        }
        else {
            !isUndefined(key) && onSelectedChanged && onSelectedChanged(key);
        }
    }, [onSelectedChanged, selectedKey, multi]);
    const filterTextChangedCallback = useCallback(debounce((value) => {
        setLoweredDebouncedSearchText(value && value.length ? toLower(value) : "");
    }, 350), []);
    const handleFilterTextChanged = useCallback(({ value }) => {
        setSearchText(value);
        filterTextChangedCallback(value);
    }, []);
    const buildHandleMouseEnter = useCallback((subMenuSettingRefKey) => () => {
        var _a, _b, _c;
        const subMenuPopover = (_c = (_b = (_a = subMenuSettingRefs.current[subMenuSettingRefKey]) === null || _a === void 0 ? void 0 : _a.ref) === null || _b === void 0 ? void 0 : _b.current) === null || _c === void 0 ? void 0 : _c.instance;
        if (!subMenuPopover)
            return;
        subMenuPopover.show();
    }, []);
    const buildHandleMouseLeave = useCallback((subMenuSettingRefKey) => () => {
        var _a, _b, _c, _d;
        if (!((_a = subMenuSettingRefs.current[subMenuSettingRefKey]) === null || _a === void 0 ? void 0 : _a.hideOnMouseLeave))
            return;
        const subMenuPopover = (_d = (_c = (_b = subMenuSettingRefs.current[subMenuSettingRefKey]) === null || _b === void 0 ? void 0 : _b.ref) === null || _c === void 0 ? void 0 : _c.current) === null || _d === void 0 ? void 0 : _d.instance;
        if (!subMenuPopover)
            return;
        subMenuPopover.hide();
    }, []);
    const handleApplyClick = useCallback(() => {
        onApply && onApply();
    }, [onApply]);
    const handleCancelClick = useCallback(() => {
        onCancel && onCancel();
    }, [onCancel]);
    const renderItem = (index, key, innerItem, active, data) => {
        var _a, _b, _c, _d;
        const menuItem = data;
        if (menuItem === null || menuItem === void 0 ? void 0 : menuItem.divider)
            return React.createElement("li", { key: `${index}_divider`, className: "divider" });
        const href = menuItem === null || menuItem === void 0 ? void 0 : menuItem.href;
        const hasSubMenu = Boolean(!multi && (menuItem === null || menuItem === void 0 ? void 0 : menuItem.subMenu));
        const uniqClassName = `${menuId}_${index}`;
        if (hasSubMenu) {
            if (subMenuSettingRefs.current[uniqClassName]) {
                (_c = (_b = (_a = subMenuSettingRefs.current[uniqClassName].ref) === null || _a === void 0 ? void 0 : _a.current) === null || _b === void 0 ? void 0 : _b.instance) === null || _c === void 0 ? void 0 : _c.hide();
                subMenuSettingRefs.current[uniqClassName].hideOnMouseLeave = true;
            }
            else {
                subMenuSettingRefs.current[uniqClassName] = {
                    hideOnMouseLeave: true,
                    ref: React.createRef()
                };
            }
        }
        return (React.createElement("li", { role: "none", className: classNames(active && !multi && "active", (menuItem === null || menuItem === void 0 ? void 0 : menuItem.disabled) && "inactive"), key: toString(key || uuid()), onClick: buildHandleMenuItemClick(key, active, data, uniqClassName), onMouseEnter: hasSubMenu ? buildHandleMouseEnter(uniqClassName) : undefined, onMouseLeave: hasSubMenu ? buildHandleMouseLeave(uniqClassName) : undefined },
            React.createElement(href ? "a" : "div", {
                href,
                tabIndex: -1,
                title: isString(innerItem) ? innerItem : "",
                className: classNames("dropdown-menu-item no-text-decoration", uniqClassName, itemClassName, itemNoWrapText ? "nowrap" : "normal-wrap", multi && "grid-cols-auto-1fr v-center-items with-gap-xs", hasSubMenu && "grid-cols-1fr-auto v-center-items with-gap-xxxs")
            }, multi && React.createElement(CheckBox, { value: active, disabled: menuItem === null || menuItem === void 0 ? void 0 : menuItem.disabled }), isString(innerItem) && MenuItemComponent ? (React.createElement(MenuItemComponent, { value: data, textSelector: textSelector, allMenuItem: allMenuItem, allMenuItemText: allMenuItemText })) : (innerItem), hasSubMenu && React.createElement("i", { className: "icon-arrow-right text-muted" })),
            (menuItem === null || menuItem === void 0 ? void 0 : menuItem.subMenu) && hasSubMenu && (React.createElement(Popover, { popoverRef: (_d = subMenuSettingRefs.current[uniqClassName]) === null || _d === void 0 ? void 0 : _d.ref, popoverClassName: classNames("popover-as-dropdown", subMenuPopoverClassName), zIndex: subMenuZIndex, target: `.${uniqClassName}`, container: `.${uniqClassName}`, position: defaultSubMenuMenuPosition, hideOnParentScroll: true, hideOnOutsideClick: true, ignoreHidingWhenChanged: true, deferRendering: false, animation: subMenuAnimation, popoverContent: menuItem.subMenu, onHidden: menuItem.onSubMenuHidden }))));
    };
    const filteredItems = useMemo(() => filter(items, e => {
        if (visibilitySelector && !visibilitySelector(e))
            return false;
        if (!loweredDebouncedSearchText)
            return true;
        const renderResult = textSelector(e);
        return (isString(renderResult) &&
            includes(toLower(renderResult), loweredDebouncedSearchText));
    }), [items, loweredDebouncedSearchText, visibilitySelector, textSelector]);
    const hasInput = !isNil(showSearchInputWhenItemsLengthIsGreaterThan) &&
        items &&
        items.length > showSearchInputWhenItemsLengthIsGreaterThan;
    const style = useMemo(() => ({
        height,
        maxHeight,
        minHeight,
        maxWidth,
        display: "grid",
        gridAutoRows: "1fr"
    }), [height, maxHeight, minHeight, maxWidth]);
    const dropdownMenuStyle = useMemo(() => ({
        minWidth
    }), [minWidth]);
    const handleModalShown = useCallback(() => {
        modalShownCompletedRef.current = true;
    }, []);
    const handleModalHidden = useCallback((e) => {
        var _a;
        if (modalShownCompletedRef.current) {
            handleModalHide();
        }
        else if ((_a = e === null || e === void 0 ? void 0 : e.component) === null || _a === void 0 ? void 0 : _a.show) {
            e.component.show();
        }
    }, []);
    const modalFooter = useMemo(() => (React.createElement("div", { className: "d-flex justify-content-flex-end with-gap-8 pt-0" },
        React.createElement(Button, { className: "btn-h-m", onClick: handleModalHide, minWidth: 101, height: 36 }, locale.MODAL_CANCEL),
        React.createElement(Button, { className: "btn-primary btn-h-m ", onClick: () => {
                (modal === null || modal === void 0 ? void 0 : modal.callback) && (modal === null || modal === void 0 ? void 0 : modal.callback());
                handleModalHide();
            }, minWidth: 136 }, locale.MODAL_OK))), [modal, handleModalHide]);
    return (React.createElement("ul", { id: menuId, className: classNames("dropdown-menu", className), style: dropdownMenuStyle, ...other },
        hasInput && (React.createElement("li", { className: "d-block" },
            React.createElement(TextBox, { className: searchInputMargins ? "ml-0_5 mt-0_5 mr-0_5" : "mt--1px ml--1px mr--1px", valueChangeEvent: "input", mode: "search", onValueChanged: handleFilterTextChanged, value: searchText, height: 34, stylingMode: searchStylingMode, placeholder: locale.SEARCH_INPUT_PLACEHOLDER }))),
        React.createElement("li", { style: style, className: "overflow-hidden" },
            filteredItems.length > 0 && (React.createElement(Scrollbars, { showScrollbar: "always", height: "100%" },
                React.createElement(Styled.ItemsWrapper, { style: dropdownMenuStyle, className: "dropdown-menu menu-items-wrapper" },
                    allMenuItem && renderItem(-1, undefined, allMenuItemText, getIsActive(undefined)),
                    allMenuItem && React.createElement("li", { key: "all-divider", className: "divider" }),
                    map(filteredItems, (e, ind) => {
                        const key = keySelector(e);
                        const renderMenuItem = e.renderElement;
                        return renderItem(ind, key, renderMenuItem ? renderMenuItem() : textSelector(e), getIsActive(key), e);
                    })))),
            (items === null || items === void 0 ? void 0 : items.length) === 0 && emptyMessage && (React.createElement(WelcomePanel, { message: emptyMessage, showIcon: false, messageClassName: "font-h5" }))),
        items && withApplyButton && (React.createElement("div", { className: "caption-with-actions-panel h-center-content with-gap-8 pl-0_25 pb-0_75 pr-0_25" },
            React.createElement(Button, { onClick: handleCancelClick, className: "btn btn-default mt-0_75" }, locale.MODAL_CANCEL),
            React.createElement(Button, { onClick: handleApplyClick, className: "btn btn-primary mt-0_75" }, locale.APPLY))),
        React.createElement(WithModal, { show: Boolean(modal === null || modal === void 0 ? void 0 : modal.callback), onHide: handleModalHidden, onShow: handleModalShown, shadingColor: shadingLightestColor, className: "bg-blur", width: 400, height: 170, resizeEnabled: false, showTitle: Boolean(modal.header), closeBtn: true, title: modal.header, footerClassName: "no-border", footer: modalFooter },
            React.createElement("div", { className: "panel-body-pl panel-body-pr pt-0_75 text-light-on-surface-variant" }, modal === null || modal === void 0 ? void 0 : modal.title))));
}
MenuList.defaultProps = {
    searchInputMargins: true
};
